import {Injectable, Injector} from '@angular/core';
import {ControleVisualService, FooterService, McticHttpClient, MessageService, SidenavItem, SidenavService} from '@mctic/ng-core';
import {KeycloakService} from 'keycloak-angular';
import {BehaviorSubject, forkJoin, Observable, of } from 'rxjs';import {switchMap} from 'rxjs/operators';
import messages from '../messages';
import {SecurityService} from '../service';
import {VersaoService} from '../service/versao.service';
import {PerfilCorporativoVO, UsuarioAutenticado } from '../shared';
import {SigplaniModuloEnum} from '../shared/enum/sigplani-modulo.enum';
import {sidenav,} from '../sidenav/sidenav-guards';
import {menusFiscalizacao, menusGerenciamento, menusInicial, menusRda} from '../sidenav/sidenav-item.enum';
import { PerfilCorporativoService } from '../service/perfil-corporativo.service';

@Injectable({
  providedIn: 'root'
})
export class ConfiguracaoService {
  private readonly service = '/sepin-backend/rest/configuracoes';
  private usuarioAutenticado: UsuarioAutenticado;
private perfilList: BehaviorSubject<PerfilCorporativoVO[]> = new BehaviorSubject<PerfilCorporativoVO[]>(null);

  constructor(
    private readonly messageService: MessageService,
    private readonly sidenavService: SidenavService,
    private readonly securityService: SecurityService,
    private readonly keycloakService: KeycloakService,
    private readonly controleVisualService: ControleVisualService,
    private readonly versaoService: VersaoService,
    private readonly footerService: FooterService,
    private readonly injector: Injector,
    private readonly http: McticHttpClient,
    private readonly perfilCorporativoService: PerfilCorporativoService
  ) { }

  initialize(): Observable<boolean> {
    return new Observable<boolean>(subscriber => {
      forkJoin(
        this.carregarConfiguracoesTela(),
        this.carregarMensagens(),
        this.carregarVersaoBackend(),
        this.carregarUsuarioAutenticado()
      ).subscribe(
        responses => {
          this.securityService.limparCacheAutenticado();
          if (responses.filter(status => !status).length > 0) {
            subscriber.error('sistemaIndisponivel');
            subscriber.complete();
          }
        },
        error => {
          subscriber.error(error);
          subscriber.complete();
        }
      );
    });
  }

  private carregarMensagens(): Observable<boolean> {
    return new Observable(subscriber => {
      this.messageService.messages = messages;
      subscriber.next(true);
      subscriber.complete();
    });
  }

  private carregarVersaoBackend(): Observable<boolean> {
    return new Observable(subscriber => {
      this.versaoService.obterVersaoBackend().subscribe(
        value => {
          this.footerService.footer.backendVersion = value.versao;
          subscriber.next(true);
          subscriber.complete();
        },
        () => {
          subscriber.error('sistemaIndisponivel');
          subscriber.complete();
        }
      );
    });
  }

  private carregarUsuarioAutenticado(): Observable<boolean> {
    return new Observable(subscriber => {
      this.securityService.obterUsuarioAutenticado().subscribe(
        usuario => {
          this.usuarioAutenticado = usuario;
          this.securityService.atualizarUsuarioAutenticadoToken(usuario);
        },
        () => {
          subscriber.next(false);
          subscriber.complete();
        }
      );
    });
  }

  setPerfilList(perfilCorporativoVO: PerfilCorporativoVO[]) {
    this.perfilList.next(perfilCorporativoVO);
  }

  carregarAutorizacao() {
    return this.http.post(`${this.service}/autorizacao`, null);
  }

  carregarConfiguracoesTela(): Observable<any> {
    if (this.securityService.usuarioSessao) {
      return this.carregarControleVisual().pipe(
        switchMap(() => this.carregarSidenav())
      );
    }
    return of(true);
  }

  carregarControleVisual(): Observable<boolean> {
    return new Observable(subscriber => {
      const usernameObservable = new Observable<string>(usernameSubscriber => {
        this.keycloakService.loadUserProfile().then(
          usuario => {
            usernameSubscriber.next(usuario.username);
            usernameSubscriber.complete();
          },
          error => {
            usernameSubscriber.error(error);
            usernameSubscriber.complete();
          }
        );
      });

      this.controleVisualService.connectUsername(usernameObservable);
      this.controleVisualService.clearCache();

      this.controleVisualService.fetchMenus().subscribe(
        menus => {
          this.sidenavService.connect(of(this.filtrarMenusPorModulo(menus)));
          subscriber.next(true);
          subscriber.complete();
        },
        () => {
          subscriber.next(false);
          subscriber.complete();
        }
      );
    });
  }

  private filtrarMenusPorModulo(menus: SidenavItem[]): SidenavItem[] {
    if (this.securityService.isModuloAtivo(SigplaniModuloEnum.RDA)) {
      return menus.filter(m => menusRda.some(mf => mf === m.titulo));
    }
    if (this.securityService.isModuloAtivo(SigplaniModuloEnum.FISCALIZACAO)) {
      return menus.filter(m => menusFiscalizacao.some(mf => mf === m.titulo));
    }
    if (this.securityService.isModuloAtivo(SigplaniModuloEnum.GERENCIAMENTO)) {
      return menus.filter(m => menusGerenciamento.some(mf => mf === m.titulo));
    }
    return menus.filter(m => menusInicial.some(mf => mf === m.titulo));
  }

  carregarSidenav(): Observable<boolean> {
    return new Observable(subscriber => {
      this.sidenavService.mapFilters(
        sidenav.map(o => this.injector.get(o))
      );
      subscriber.next(true);
      subscriber.complete();
    });
  }
}
